/**
 * 🔒 QUICK-STOCK-2026-05-18 — Consultar Stock Dialog
 *
 * Permite a admin/gerente/propietario/cajero escanear un código de barras
 * o tipearlo manualmente y ver INSTANTÁNEAMENTE:
 *
 *   - Si es producto: precio venta, costo, simple_inventory_qty, recipe_active
 *   - Si es ingrediente: stock total + stock por almacén + dual-unit
 *
 * Flujo:
 *   1) Abre dialog → muestra scanner (cámara o manual)
 *   2) Escanea barcode → llama /api/quick-stock-check/{barcode}
 *   3) Muestra resultado en card minimalista
 *   4) Botón "Escanear otro" vuelve al scanner sin cerrar el dialog
 *
 * Reusa: BarcodeScanner (iframe sandboxed) + QuickAssignBarcodeDialog (cuando no encuentra)
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PackageSearch, Boxes, ShoppingBag, Loader2, ArrowLeft, AlertCircle, Warehouse } from 'lucide-react';
import { notify } from '@/lib/notify';
import BarcodeScanner from '@/components/BarcodeScanner';
import QuickAssignBarcodeDialog from '@/components/QuickAssignBarcodeDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('pos_token')}` });

function formatMoney(n) {
  const v = Number(n) || 0;
  return `RD$ ${v.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatStock(n) {
  const v = Number(n) || 0;
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}

export default function QuickStockCheckDialog({ open, onOpenChange }) {
  const [view, setView] = useState('scanner'); // 'scanner' | 'result' | 'not-found'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastBarcode, setLastBarcode] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);

  // Reset al cerrar
  const handleOpenChange = (next) => {
    if (!next) {
      setView('scanner');
      setResult(null);
      setLoading(false);
      setLastBarcode('');
      setAssignOpen(false);
    }
    onOpenChange(next);
  };

  const lookup = useCallback(async (barcode) => {
    const code = (barcode || '').trim();
    if (!code) return;
    setLastBarcode(code);
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/quick-stock-check/${encodeURIComponent(code)}`, {
        headers: authHdr(),
      });
      setResult(resp.data);
      setView('result');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setResult(null);
        setView('not-found');
      } else if (status === 403) {
        notify.error('Sin permiso para consultar stock', {
          description: 'Tu rol no tiene el permiso check_stock activo.',
        });
        handleOpenChange(false);
      } else {
        notify.error('Error al consultar stock', { description: e?.message || 'Intenta de nuevo' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleScan = useCallback((value) => {
    lookup(value);
  }, [lookup]);

  const handleScanAnother = () => {
    setView('scanner');
    setResult(null);
    setLastBarcode('');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto"
          data-testid="quick-stock-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageSearch size={20} className="text-blue-500" />
              Consultar Stock
            </DialogTitle>
            <DialogDescription>
              Escanea un código de barras para ver el inventario disponible.
            </DialogDescription>
          </DialogHeader>

          {/* SCANNER */}
          {view === 'scanner' && (
            <div className="space-y-3" data-testid="quick-stock-scanner-view">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                  <Loader2 size={32} className="animate-spin text-blue-500" />
                  <span className="text-sm">Buscando código <span className="font-mono">{lastBarcode}</span>...</span>
                </div>
              ) : (
                <BarcodeScanner
                  isActive={open && view === 'scanner'}
                  onScan={handleScan}
                  onManualEntry={(code) => lookup(code)}
                />
              )}
            </div>
          )}

          {/* RESULT */}
          {view === 'result' && result && (
            <div className="space-y-3" data-testid="quick-stock-result-view">
              {result.kind === 'product' && (
                <div className="space-y-3" data-testid="quick-stock-product">
                  <div className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <ShoppingBag size={16} className="text-blue-500" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Producto</span>
                    </div>
                    <div className="font-bold text-base" data-testid="qs-product-name">{result.name}</div>
                    {result.category && <div className="text-xs text-muted-foreground mt-0.5">{result.category}</div>}
                    <div className="text-xs font-mono text-muted-foreground mt-1">{result.barcode}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-card border border-border rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Precio venta</div>
                      <div className="text-lg font-bold mt-1 font-mono" style={{ color: '#E8630A' }} data-testid="qs-product-price">
                        {formatMoney(result.sale_price)}
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Costo</div>
                      <div className="text-lg font-bold mt-1 font-mono text-foreground" data-testid="qs-product-cost">
                        {result.cost_price > 0 ? formatMoney(result.cost_price) : '—'}
                      </div>
                    </div>
                  </div>

                  {result.recipe_active ? (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-2" data-testid="qs-product-recipe">
                      <Boxes size={16} className="text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Receta activa — stock se descuenta de ingredientes</span>
                    </div>
                  ) : result.track_simple_inventory ? (
                    <div className="bg-card border-2 border-blue-500 rounded-xl p-3" data-testid="qs-product-simple-stock">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Stock simple disponible</div>
                      <div className="text-2xl font-bold mt-1 font-mono text-blue-600 dark:text-blue-400">
                        {formatStock(result.simple_inventory_qty)} unidades
                      </div>
                    </div>
                  ) : (
                    <div className="bg-card border border-border rounded-xl p-3 text-center text-muted-foreground text-sm" data-testid="qs-product-no-stock">
                      Sin inventario configurado para este producto.
                    </div>
                  )}
                </div>
              )}

              {result.kind === 'ingredient' && (
                <div className="space-y-3" data-testid="quick-stock-ingredient">
                  <div className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Boxes size={16} className="text-orange-500" />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Ingrediente</span>
                    </div>
                    <div className="font-bold text-base" data-testid="qs-ing-name">{result.name}</div>
                    {result.category && <div className="text-xs text-muted-foreground mt-0.5">{result.category}</div>}
                    <div className="text-xs font-mono text-muted-foreground mt-1">{result.barcode}</div>
                  </div>

                  {/* Totales */}
                  <div className="bg-card border-2 border-orange-500 rounded-xl p-3" data-testid="qs-ing-totals">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Stock total</div>
                    <div className="text-2xl font-bold mt-1 font-mono" style={{ color: '#E8630A' }}>
                      {formatStock(result.total_stock_dispatch)} {result.unit}
                    </div>
                    {result.conversion_factor > 1 && result.purchase_unit && (
                      <div className="text-xs font-mono text-muted-foreground mt-1">
                        ≈ {formatStock(result.total_stock_purchase)} {result.purchase_unit}
                      </div>
                    )}
                  </div>

                  {/* Stock por almacén */}
                  {result.stock_by_warehouse.length > 0 && (
                    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="qs-ing-warehouses">
                      <div className="px-3 py-2 bg-[#1A1A2E]" ref={el => { if (el) el.style.setProperty('color', '#ffffff', 'important'); }}>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                          <Warehouse size={14} /> Por almacén
                        </div>
                      </div>
                      <div className="divide-y divide-border">
                        {result.stock_by_warehouse.map((s, i) => (
                          <div key={i} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`qs-wh-row-${i}`}>
                            <span className="font-medium">{s.warehouse_name}</span>
                            <span className="font-mono font-bold">{formatStock(s.current_stock)} {result.unit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Min stock warning */}
                  {result.min_stock > 0 && result.total_stock_dispatch < result.min_stock && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-2 flex items-center gap-2" data-testid="qs-ing-low-stock">
                      <AlertCircle size={14} className="text-red-600 dark:text-red-400" />
                      <span className="text-xs font-semibold text-red-700 dark:text-red-400">
                        Bajo el mínimo ({formatStock(result.min_stock)} {result.unit})
                      </span>
                    </div>
                  )}

                  <div className="text-[11px] text-muted-foreground text-right font-mono">
                    Costo unitario: {formatMoney(result.avg_cost)}
                  </div>
                </div>
              )}

              <Button onClick={handleScanAnother} variant="outline" className="w-full" data-testid="qs-scan-another-btn">
                <ArrowLeft size={16} className="mr-1" /> Escanear otro código
              </Button>
            </div>
          )}

          {/* NOT FOUND */}
          {view === 'not-found' && (
            <div className="space-y-3" data-testid="quick-stock-not-found">
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4 text-center">
                <AlertCircle size={32} className="text-red-500 mx-auto mb-2" />
                <div className="font-bold text-base">Código no encontrado</div>
                <div className="text-xs font-mono text-muted-foreground mt-1">{lastBarcode}</div>
                <div className="text-xs text-muted-foreground mt-2">
                  No existe ningún producto ni ingrediente con este código de barras.
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleScanAnother} variant="outline" className="flex-1" data-testid="qs-retry-btn">
                  <ArrowLeft size={16} className="mr-1" /> Escanear otro
                </Button>
                <Button onClick={() => setAssignOpen(true)} className="flex-1" data-testid="qs-assign-btn">
                  Asignar / Crear
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <QuickAssignBarcodeDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        barcode={lastBarcode}
        scanMode="mixed"
        onAssigned={() => {
          setAssignOpen(false);
          // Tras asignar, vuelve a buscar el código
          lookup(lastBarcode);
        }}
        onCreateNew={() => {
          setAssignOpen(false);
          handleOpenChange(false);
          // Idealmente navegar a Insumos con state. Por ahora cerrar y dejar al usuario.
          notify.info('Crea el nuevo insumo desde Inventario → Insumos.');
        }}
      />
    </>
  );
}
